import StudioBadgeSvg from '@components/Images/StudioBadgeSvg';
import { usePortfolioItemMediaSources } from '@components/Hooks/usePortfolioItemImageUrl';
import {
  PROFILE_CLIENT_PROJECTS_IMAGE_DIMENSIONS,
  PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE,
  ProfileClientProjectsLayoutType,
} from '@utils/constants';
import { PortfolioItem } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { Box, Image, MediaOverlay, Proportion, Text } from '@wix/design-system';
import { useMemo, useState, useEffect, useRef } from 'react';
import s from './Project.module.scss';

export { generateThumbnailUrl } from '@components/Hooks/usePortfolioItemImageUrl';

export interface PortfolioItemWithUrl extends PortfolioItem {
  url: string;
  isStudio?: boolean;
  isMobileView?: boolean;
  layoutType?: ProfileClientProjectsLayoutType;
  onProjectClick?: (project: PortfolioItemWithUrl) => void;
}

const Project = ({
  description,
  siteName,
  isStudio,
  isMobileView,
  layoutType = 'threeColumn',
  ...projectData
}: PortfolioItemWithUrl) => {
  const [imageLoaded, setImageLoaded] = useState(() => {
    return typeof window === 'undefined';
  });

  const imageContainerRef = useRef<HTMLDivElement>(null);

  const dimensions = useMemo(
    () =>
      layoutType === PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE.TWO_COLUMN
        ? PROFILE_CLIENT_PROJECTS_IMAGE_DIMENSIONS.TWO_COLUMN
        : PROFILE_CLIENT_PROJECTS_IMAGE_DIMENSIONS.THREE_COLUMN,
    [layoutType],
  );

  const mediaSources = usePortfolioItemMediaSources(projectData, dimensions);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const img: HTMLImageElement | null = container.querySelector('img');
    if (!img) return;

    if (img.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
    } else {
      setImageLoaded(false);
      img.onload = () => setImageLoaded(true);
    }
  }, [mediaSources.mediaSource]);

  return (
    <Box
      dataHook="portfolio-project"
      className={isMobileView ? s.projectMobile : s.project}
    >
      <Proportion aspectRatio={1.778} className={s.proportion}>
        <MediaOverlay
          hoverSkin="gradient"
          borderRadius="3px 3px 0 0"
          className={s.mediaOverlay}
          media={
            <Box
              height="100%"
              className={s.imageWrapper}
              ref={imageContainerRef}
              dataHook="project-media-image-container"
            >
              <Image
                className={`${s.projectImage} ${!imageLoaded ? s.hidden : ''}`}
                fit="cover"
                dataHook="project-media-image"
                alt={`${siteName}: ${description}`}
                borderRadius="3px 3px 0 0"
                src={mediaSources.mediaSource}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  if (mediaSources.backupSource) {
                    e.currentTarget.src = mediaSources.backupSource;
                    setImageLoaded(false);
                  }
                }}
              />

              {isStudio && (
                <Box
                  dataHook="project-studio-badge"
                  position="absolute"
                  right="6px"
                  bottom="6px"
                  zIndex="1"
                >
                  <StudioBadgeSvg />
                </Box>
              )}
            </Box>
          }
          dataHook="project-media-overlay"
        >
          <MediaOverlay.Content
            placement="bottom-start"
            visible="hover"
            data-hook="project-media-overlay-content"
          >
            <Box className={s.hoverContent}>
              <Text size="small" weight="normal" className={s.projectName}>
                {siteName}
              </Text>
            </Box>
          </MediaOverlay.Content>
        </MediaOverlay>
      </Proportion>
    </Box>
  );
};

export default Project;
